/** The relative-date units offered for `in_last` / `not_in_last` conditions. */
export type RelativeDurationUnit = 'days' | 'months' | 'years';

export interface RelativeDuration {
  /** Positive whole number. `NaN` represents the cleared/partially typed state. */
  amount: number;
  unit: RelativeDurationUnit;
}

/** The default seeded when an `in_last` / `not_in_last` operator is selected. */
export const defaultRelativeDuration: RelativeDuration = {
  amount: 30,
  unit: 'days',
};

const unitToIsoDesignator: Record<RelativeDurationUnit, string> = {
  days: 'D',
  months: 'M',
  years: 'Y',
};

const isoDesignatorToUnit: Record<string, RelativeDurationUnit> = {
  D: 'days',
  M: 'months',
  Y: 'years',
};

/**
 * Builds an ISO 8601 duration string (e.g. `"P30D"`) from a single-unit amount.
 * A non-finite `amount` yields a designator-only string (e.g. `"PD"`), which is
 * an invalid duration on purpose: it keeps the chosen unit while the value is
 * cleared, and filter validation rejects it until a number is given.
 */
export const buildRelativeDuration = ({
  amount,
  unit,
}: RelativeDuration): string => {
  const amountStr = Number.isFinite(amount) ? String(amount) : '';
  return `P${amountStr}${unitToIsoDesignator[unit]}`;
};

/**
 * Parses a single-unit ISO 8601 duration string back into an amount and unit.
 * Unrecognised strings (empty, multi-unit, time components, …) fall back to the
 * default; a present-but-empty amount is returned as `NaN` to preserve the unit
 * for a cleared input.
 */
export const parseRelativeDuration = (value: string): RelativeDuration => {
  const match = /^P(\d*(?:\.\d+)?)(Y|M|D)$/.exec(value ?? '');
  if (!match) {
    return { ...defaultRelativeDuration };
  }
  return {
    amount: match[1] === '' ? NaN : Number(match[1]),
    unit: isoDesignatorToUnit[match[2]],
  };
};

export const filterSetToExecutionFilters = (
  filterSet: Pick<FilterSet, 'filters' | 'filter_colors'>,
): ExecutionFilter[] =>
  filterSet.filters.map((filter, i) => {
    const color = filterSet.filter_colors?.[i];
    return {
      active: true,
      filter,
      ...(color ? { color } : {}),
    };
  });

/** Index-aligned with `filters`. `null` means the UI should pick a default. */
export const executionFiltersToFilterColors = (
  filters: ExecutionFilter[],
): Array<string | null> => filters.map((f) => f.color ?? null);

export const executionFiltersMatchFilterSet = (
  executedFilters: ExecutionFilter[],
  filterSet: Pick<FilterSet, 'filters' | 'filter_colors'>,
): boolean =>
  JSON.stringify(executedFilters.map((f) => f.filter)) ===
    JSON.stringify(filterSet.filters) &&
  JSON.stringify(executedFilters.map((f) => f.color ?? null)) ===
    JSON.stringify(
      filterSet.filters.map((_, i) => filterSet.filter_colors?.[i] ?? null),
    );
